<script setup lang="ts">
/**
 * InstallBanner Component
 *
 * Shows a banner prompting users to install the PWA
 * - On Chrome/Edge: Shows install button
 * - On iOS Safari: Shows instructions for manual install
 */

import { useInstallPrompt } from '@/composables/useInstallPrompt'
import Button from '@/volt/Button.vue'

const { isIOS, canInstall, shouldShowBanner, installApp, dismissBanner } = useInstallPrompt()

async function handleInstall() {
  await installApp()
}
</script>

<template>
  <Transition name="slide-down">
    <div
      v-if="shouldShowBanner"
      class="install-banner"
    >
      <div class="banner-content">
        <!-- Icon -->
        <div class="banner-icon">
          <i class="pi pi-download" />
        </div>

        <!-- Text -->
        <div class="banner-text">
          <p class="banner-title">App installieren</p>
          <p
            v-if="isIOS"
            class="banner-subtitle"
          >
            Tippe auf
            <svg
              class="ios-share-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V10c0-1.1.9-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .9 2 2z"
              />
            </svg>
            und dann "Zum Home-Bildschirm"
          </p>
          <p
            v-else
            class="banner-subtitle"
          >
            Für schnelleren Zugriff und Offline-Nutzung
          </p>
        </div>

        <!-- Actions -->
        <div class="banner-actions">
          <Button
            v-if="canInstall"
            label="Installieren"
            size="small"
            class="install-btn"
            @click="handleInstall"
          />
          <button
            class="dismiss-btn"
            aria-label="Schließen"
            @click="dismissBanner"
          >
            <i class="pi pi-times" />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.install-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 0.75rem 1rem;
  padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  background: var(--color-brand-primary);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 600px;
  margin: 0 auto;
}

.banner-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--color-on-primary);
  font-size: 1.25rem;
}

.banner-text {
  flex: 1;
  min-width: 0;
}

.banner-title {
  font-weight: 600;
  color: var(--color-on-primary);
  margin: 0;
  font-size: 0.9rem;
}

.banner-subtitle {
  font-size: 0.75rem;
  color: var(--color-on-primary);
  opacity: 0.9;
  margin: 0;
}

.banner-subtitle .pi {
  font-size: 0.7rem;
  vertical-align: middle;
}

.ios-share-icon {
  width: 1em;
  height: 1em;
  vertical-align: middle;
  display: inline-block;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.dismiss-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: var(--color-on-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.dismiss-btn:hover {
  background: rgba(0, 0, 0, 0.2);
}

.install-btn {
  background: rgba(0, 0, 0, 0.1);
  color: var(--color-on-primary);
  border: none;
  box-shadow: none;
}

.install-btn:hover {
  background: rgba(0, 0, 0, 0.18);
}

.install-btn:active {
  background: rgba(0, 0, 0, 0.22);
}

.install-btn:focus-visible {
  outline: 2px solid rgba(0, 0, 0, 0.35);
  outline-offset: 2px;
}

/* Slide animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
